import { mapMutations, mapGetters } from "vuex";
import {
  eachDay,
  getTime,
  format,
  subDays,
  differenceInDays,
  isSameDay,
  addDays,
  subYears,
  startOfDay
} from "date-fns";

export default {
  data() {
    return {
      dayFilter: 7,
      chartKey: 0,
      existingEntries: [],
      days: [],
      filterEndDate: startOfDay(new Date()),
      filterStartDate: startOfDay(subDays(new Date(), 6)),
      today: new Date(),
      showCustomDateFilter: false,
      customStartDate: "",
      customEndDate: "",
      customSearch: false,
      addNewEntry: false
    };
  },

  computed: {
    ...mapGetters(["getDB", "getUID", "getPainEntries"]),

    // Make module a computed property so it updates when route changes
    module() {
      return this.$route.params.module;
    },

    oneYearAgo() {
      return format(subYears(new Date(), 1), "YYYY-MM-DD");
    },

    todayFormatted() {
      return format(new Date(), "YYYY-MM-DD");
    },

    // Checks to see if the end filter date and today are the same day.
    isFilterEndToday() {
      return isSameDay(new Date(), this.filterEndDate);
    }
  },

  methods: {
    ...mapMutations([
      "modifyLoading",
      "setUID",
      "addPainEntry",
      "clearPainEntries"
    ]),

    // Scrolls user to the top of opened accordion.
    // Summary element needs click event and ID.
    openAccordion({ target }) {
      if (target.nodeName !== "SUMMARY") {
        target = target.closest("summary");
      }

      setTimeout(() => {
        const elementID = document.getElementById(target.id);
        const parent = elementID.parentElement;

        if (parent.open) {
          parent.scrollIntoView({
            behavior: "smooth"
          });
        }
      }, 100);
    },

    /**
     * Disable loader with artifical pause.
     *
     * @param integer time
     *  Amount of time to delay the loader animation.
     */
    disableLoader(time) {
      setTimeout(() => {
        this.modifyLoading(false);
      }, time);
    },

    // Runs when date filters are changed.
    filterUpdate(days, multiple = false) {
      // Reset start and end dates.
      this.filterEndDate = startOfDay(new Date());
      this.filterStartDate = startOfDay(subDays(new Date(), days - 1));

      // Set custom search to false.
      this.customSearch = false;
      this.showCustomDateFilter = false;

      // Update pain entries.
      this.firestoreGetPainEntries(this.filterStartDate, this.filterEndDate);

      // Update the data.
      this.updateDays(
        days,
        this.filterStartDate,
        this.filterEndDate,
        this.module,
        multiple
      );
    },

    filterCustomDates(multiple = false) {
      this.filterStartDate = new Date(`${this.customStartDate} 00:00:00`);
      this.filterEndDate = new Date(`${this.customEndDate} 00:00:00`);

      this.customSearch = true;

      this.dayFilter = differenceInDays(
        this.filterEndDate,
        this.filterStartDate
      );

      this.updateDays(
        this.dayFilter,
        this.filterStartDate,
        this.filterEndDate,
        this.module,
        multiple
      );
    },

    // Load previous set of dates.
    previousDates(multiple = false) {
      // Adjust date change text.
      this.filterEndDate = this.filterStartDate;
      this.filterStartDate = subDays(this.filterEndDate, this.dayFilter - 1);

      // Update the days data.
      this.updateDays(
        this.dayFilter,
        this.filterStartDate,
        this.filterEndDate,
        this.module,
        multiple
      );
    },

    // Load next set of dates.
    nextDates(multiple = false) {
      // Adjust date change text.
      this.filterStartDate = this.filterEndDate;
      this.filterEndDate = addDays(this.filterStartDate, this.dayFilter - 1);

      // Update the days data.
      this.updateDays(
        this.dayFilter,
        this.filterStartDate,
        this.filterEndDate,
        this.module,
        multiple
      );
    },

    /**
     * Sets days and queries for entries.
     *
     * @param integer days
     *  Number of days to query for.
     */

    async updateDays(days, startDate, endDate, module, multiple = false) {
      // Fire up the loader.
      this.modifyLoading(true);

      // Query the database for matching entries.
      await this.queryExistingEntries(
        module,
        startDate,
        endDate,
        days,
        multiple
      );

      // Disable the loader.
      this.disableLoader(1000);
    },

    /**
     * Formats the provided date nicely.
     *
     * @param Date date
     */
    formatDate(date) {
      return format(date, "MMMM D, YYYY");
    },

    /**
     * Changes the days data array to the provided number of days.
     */
    buildDays(days, startDate, endDate) {
      this.days = [];

      const allDays = eachDay(startDate, endDate);

      allDays.forEach(day => {
        this.days.push({
          date: day,
          timestamp: getTime(day),
          showForm: true,
          edit: false,
          delete: false
        });
      });

      this.days.reverse();
    },

    toggleCustomDateFilter() {
      this.showCustomDateFilter = !this.showCustomDateFilter;
    },

    // Grabs all pain entries for the user.
    firestoreGetPainEntries(startDate, endDate) {
      return this.getDB
        .collection("logs")
        .where("user", "==", this.getUID)
        .where("module", "==", "pain")
        .where("date", ">=", startDate)
        .where("date", "<=", endDate)
        .orderBy("date", "desc")
        .onSnapshot(querySnapshot => {
          this.clearPainEntries();

          querySnapshot.forEach(doc => {
            this.addPainEntry(doc.data());
          });
        });
    },

    /**
     * Fetches existing log entries from Firestore.
     *
     * @param string module
     * @param Date startDate
     * @param Date endDate
     */
    queryExistingEntries(module, startDate, endDate, days, multiple = false) {
      return this.getDB
        .collection("logs")
        .where("user", "==", this.getUID)
        .where("module", "==", module)
        .where("date", ">=", startDate)
        .where("date", "<=", endDate)
        .orderBy("date", "desc")
        .onSnapshot(querySnapshot => {
          // Clear out our existing entries.
          this.existingEntries = [];

          // Set the day filter.
          this.dayFilter = days;

          // Filter the days.
          this.buildDays(days, startDate, endDate);

          querySnapshot.forEach(doc => {
            const id = { id: doc.id };

            // Add the doc ID to the data object.
            const docWithID = Object.assign(id, doc.data());

            // Merge our id object into the data object.
            this.existingEntries.push(docWithID);

            // Get the appropriate timestamp from our DB record.
            const entryDate = docWithID.date.seconds * 1000;

            // Find the index of the matching day.
            const matchingIndex = this.days.findIndex(
              day => day.timestamp == entryDate
            );

            // Store entries as an array if multiple is true.
            if (multiple) {
              // Create empty array if one isn't there yet.
              if (!this.days[matchingIndex].entry) {
                this.days[matchingIndex].entry = [];
              }

              // Add our doc to the entry array.
              this.days[matchingIndex].entry.push(docWithID);
            } else {
              // Store entry as a single object is multiple is false;
              this.days[matchingIndex].entry = docWithID;
            }

            // Add the entry to our day and hide the form.
            this.days[matchingIndex].showForm = false;
          });
        });
    },

    /**
     * Creates a new entry in Firestore.
     *
     * @param string module
     * @param object data
     */
    createLogEntry(module, data, day) {
      this.getDB.collection("logs").add({
        user: this.getUID,
        module: module,
        date: day,
        data: data
      });
    },

    /**
     * Deletes an entry from Firestore.
     *
     * @param string module
     * @param Date day
     */
    deleteLogEntry(module, day) {
      this.getDB
        .collection("logs")
        .where("user", "==", this.getUID)
        .where("module", "==", module)
        .where("date", "==", day)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            doc.ref.delete();
          });
        });
    }
  }
};
